import React from 'react';
import styled from 'styled-components';
import { Notification } from '../../types/notification';
import UserAvatar from '../common/UserAvatar';
import { useAppContext } from '../../context/AppContext';
import { getReactIconForCategory } from '../../utils/iconMigration';
import * as FaIcons from 'react-icons/fa';

interface NotificationItemProps {
  notification: Notification;
  isRead: boolean;
  showUserAvatar?: boolean;
  onClick?: () => void;
  isHistory?: boolean; // Nouveau prop pour identifier les notifications d'historique
}

const NotificationItemContainer = styled.div<{ $isRead: boolean; $isUpcoming?: boolean; $isHistory?: boolean }>`
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  background-color: ${({ $isRead, $isUpcoming, $isHistory, theme }) => {
    if ($isHistory) {
      return 'rgba(52, 152, 219, 0.15)'; // Bleu atténué pour l'historique
    }
    if ($isUpcoming) {
      return 'rgba(255, 180, 0, 0.15)'; // Jaune-orangé atténué et translucide pour les rappels à venir
    }
    return $isRead ? theme.colors.backgroundLight : 'rgba(255, 80, 80, 0.15)';
  }};
  cursor: ${({ onClick }) => onClick ? 'pointer' : 'default'};
  transition: background-color 0.2s;
  border-left: ${({ $isRead, $isUpcoming, $isHistory }) => {
    if ($isHistory) {
      return '3px solid rgba(52, 152, 219, 0.5)'; // Bordure bleue pour l'historique
    }
    if ($isUpcoming) {
      return '3px solid rgba(255, 180, 0, 0.5)'; // Bordure jaune-orangée pour les rappels à venir
    }
    return $isRead ? 'none' : '3px solid rgba(255, 80, 80, 0.5)';
  }};
  
  &:hover {
    background-color: ${({ $isRead, $isUpcoming, $isHistory, theme }) => {
      if ($isHistory) {
        return 'rgba(52, 152, 219, 0.25)'; // Bleu plus intense au survol pour l'historique
      }
      if ($isUpcoming) {
        return 'rgba(255, 180, 0, 0.25)'; // Légèrement plus intense au survol pour les rappels à venir
      }
      return $isRead ? theme.colors.border : 'rgba(255, 80, 80, 0.25)';
    }};
  }
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: bold;
  margin-bottom: 4px;
`;

const CategoryTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CategoryIcon = styled.span<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || '#e0e0e0'};
  color: white;
  font-size: 12px;
`;

const NotificationBody = styled.div`
  margin-bottom: 4px;
`;

const NotificationFooter = styled.small`
  display: block;
  color: ${({ theme }) => theme.colors.textLight};
`;

const NotificationAddress = styled.small`
  display: block;
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ThumbnailContainer = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

/**
 * Composant pour afficher un élément de notification avec l'avatar de l'utilisateur
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  isRead, 
  showUserAvatar = true,
  onClick,
  isHistory = false
}) => {
  // Accéder au contexte de l'application pour obtenir les catégories et les marqueurs
  const { state } = useAppContext();
  
  // Déterminer si c'est une notification à venir (un rappel futur)
  const isUpcoming = notification.type === 'reminder' && 
                    notification.reminderDate && 
                    new Date(notification.reminderDate) > new Date();
  
  // Trouver la catégorie correspondant au nom de la catégorie dans la notification
  const category = state.categories.find(cat => cat.name === notification.markerCategory);
  
  // Trouver le marqueur associé à la notification
  const marker = notification.markerId ? state.markers.find(m => m.id === notification.markerId) : null;
  
  // Obtenir l'icône de la catégorie
  const IconComponent = category ? getReactIconForCategory(category.icon) : FaIcons.FaHome;
  
  // Obtenir la couleur de la catégorie ou utiliser une couleur par défaut
  // Pour l'historique, forcer la couleur bleue
  const categoryColor = isHistory ? '#3498db' : (category ? category.color : '#808080');
  
  return (
    <NotificationItemContainer
      $isRead={isRead}
      $isUpcoming={isUpcoming}
      $isHistory={isHistory}
      onClick={onClick ? () => onClick() : undefined}
    >
      <NotificationContent>
      <NotificationHeader>
        <CategoryTitle>
          <CategoryIcon $color={categoryColor}>
            {IconComponent && <IconComponent size={12} />}
          </CategoryIcon>
          {notification.markerCategory}
        </CategoryTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {showUserAvatar ? (
            <>
              {notification.user_id ? (
                <UserAvatar 
                  userId={notification.user_id} 
                  size={24}
                />
              ) : (
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  backgroundColor: '#eee', 
                  fontSize: '10px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '1px solid #ccc'
                }}>?</div>
              )}
            </>
          ) : null}
        </div>
      </NotificationHeader>
      
      <NotificationBody>
        {notification.title} - {notification.message}
      </NotificationBody>
      
      <NotificationFooter>
        {notification.type === 'reminder' && notification.reminderDate 
          ? `Prévu pour le : ${new Date(notification.reminderDate).toLocaleString()}`
          : `Créé le : ${new Date(notification.createdAt).toLocaleString()}`
        }
      </NotificationFooter>
      
      {notification.address && (
        <NotificationAddress>
          📍 {notification.address}
        </NotificationAddress>
      )}
      </NotificationContent>
      
      {/* Vignette de la photo du marqueur à droite */}
      {marker?.imageUrl && (
        <ThumbnailContainer>
          <ThumbnailImage src={marker.imageUrl} alt="Photo du bien" />
        </ThumbnailContainer>
      )}
    </NotificationItemContainer>
  );
};

export default NotificationItem;

